import { useEffect, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { EditOutlined, FormOutlined, PlusOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTenantNavigation } from '../../../hooks/useTenantNavigation.js';
import { Divider, Flex, Input, message, Popover, Modal, Tooltip, Select } from 'antd';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSidebarData, setSidebarOpen, setSidebarType, setWorkflow, setChangeDetected } from '../../../redux/slices/connectorSlice';
import { deleteForm } from '../../../services/WorkflowServices';
import { showLoader } from '../../../utils/showloader';
import { spaceColorBgMapping, workflowIconsConfig } from '../../../utils/colorBgColorMap';
import { FORM_LANGUAGE_OPTIONS } from '../../../utils/formTranslations';
import { resolveNodeTranslation, setNodeLeafForLanguage, DEFAULT_LANGUAGE } from '../../../utils/workflowTranslations';
import SvgIcons from '../../layout/assets/SvgIcons';
import { useTranslation } from 'react-i18next';


const InitialNode = ({ data, position, id, type }) => {
    const { t, i18n } = useTranslation();
    const [nodeData, setNodeData] = useState({})
    const [stepName, setStepName] = useState('')
    const [expanded, setExpanded] = useState(false)
    // Language the inline name field is authoring. Defaults to (and follows) the
    // app language, but the in-panel dropdown lets the builder pick any language.
    // The default language edits the canonical `name`; others edit
    // data.translations[lng].name, so the canonical name / status id is preserved.
    const [editLang, setEditLang] = useState(i18n.language || DEFAULT_LANGUAGE)
    const navigate = useTenantNavigation();
    const dispatch = useDispatch();
    const { spaceId } = useParams();
    const { workflow } = useSelector(state => state.connector)

    useEffect(() => {
        setEditLang(i18n.language || DEFAULT_LANGUAGE)
    }, [i18n.language])

    // Label resolved for the language being edited (falls back to canonical name).
    const displayName = resolveNodeTranslation(data, editLang)?.name || ""

    useEffect(() => {
        setStepName(displayName)
    }, [displayName])

    useEffect(() => {
        if (workflow.workflowId) {
            let currentNode = workflow.nodes?.find(node => node.id === id);
            setNodeData(currentNode || {})
        }
    }, [workflow?.workflowId, workflow.nodes])

    const handleNameChange = (val) => {
        setStepName(val)
        let updatedWorkflow = { ...workflow }
        let updatedNodes = updatedWorkflow.nodes.map(node => {
            if (node.id === id) {
                return { ...node, data: setNodeLeafForLanguage(node.data, editLang, "name", val) }
            } else {
                return { ...node }
            }
        })
        updatedWorkflow.nodes = updatedNodes
        dispatch(setWorkflow({ ...updatedWorkflow }))
    }

    const navigateToForm = () => {
        if (!workflow.workflowId) {
            message.error(t('Please save workflow'))
            return
        }
        let formId = nodeData?.formData?.formId || "new"
        navigate("/app/space/" + spaceId + "/" + workflow.workflowId + "/" + id + "/form-editor/" + formId)
    }

    const handleIconColorChange = (key, val) => {
        let updatedNode = workflow.nodes.map(node => {
            if (node.id === id) {
                return { ...node, data: { ...node.data, [key]: val } }
            }
            return { ...node }
        })
        dispatch(setWorkflow({ ...workflow, nodes: updatedNode }))
    }

    const handleDeleteForm = async () => {
        const { nodes, edges } = workflow;
        showLoader(true)
        let deleteResponse = await deleteForm(nodeData.formData.formId)
        showLoader(false)

        if(deleteResponse.status === 200){
            let updatedNodes = nodes.map(node => {
                if(node.id === id) {
                    return {...node, formData: {formId:"", formName: ""}}
                }
                return {...node}
            })
            dispatch(setWorkflow({...workflow, nodes: updatedNodes, edges: edges}))
            dispatch(setChangeDetected(true))
        }
    }

    const confirmFormDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();

        Modal.confirm({
            title: t('Confirm'),
            content: t('Are you sure you want to delete this form?'),
            okText: t('Yes'),
            cancelText: t('No'),
            onOk: (event) => {
                handleDeleteForm()
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }
    const colorIconPicker = (
        <div className="color-icon-picker">
            <div className="picker-header">
                <div className="text-weight-600">{t('Pick color and icon')}</div>
            </div>
            <div className="color-row">
                <div className="color-box" style={{ backgroundColor: "#F5222D" }} onClick={() => handleIconColorChange("stepColor", "#F5222D")}>{data.stepColor === "#F5222D" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#FA8C16" }} onClick={() => handleIconColorChange("stepColor", "#FA8C16")}>{data.stepColor === "#FA8C16" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#FAAD14" }} onClick={() => handleIconColorChange("stepColor", "#FAAD14")}>{data.stepColor === "#FAAD14" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#84CC16" }} onClick={() => handleIconColorChange("stepColor", "#84CC16")}>{data.stepColor === "#84CC16" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#50A248" }} onClick={() => handleIconColorChange("stepColor", "#50A248")}>{data.stepColor === "#50A248" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#13C2C2" }} onClick={() => handleIconColorChange("stepColor", "#13C2C2")}>{data.stepColor === "#13C2C2" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#0A99FE" }} onClick={() => handleIconColorChange("stepColor", "#0A99FE")}>{data.stepColor === "#0A99FE" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#141BCB" }} onClick={() => handleIconColorChange("stepColor", "#141BCB")}>{data.stepColor === "#141BCB" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#722ED1" }} onClick={() => handleIconColorChange("stepColor", "#722ED1")}>{data.stepColor === "#722ED1" && <CheckOutlined style={{ color: "white" }} />}</div>
                <div className="color-box" style={{ backgroundColor: "#EB2F96" }} onClick={() => handleIconColorChange("stepColor", "#EB2F96")}>{data.stepColor === "#EB2F96" && <CheckOutlined style={{ color: "white" }} />}</div>
            </div>
            <div className="icon-picker">
                {
                    workflowIconsConfig.map(icon => {
                        return <div className="icon-picker-box" onClick={() => handleIconColorChange("stepIcon", icon)}>
                            <span style={{ color: data.stepIcon === icon ? data.stepColor : "#00000073", display: "flex" }}>
                                <SvgIcons key={"color-" + data.stepColor} type={icon} />

                            </span>
                        </div>

                    })
                }
            </div>
        </div>
    )

    const handleStartClick = () => {
        dispatch(setSidebarOpen(true));
        dispatch(setSidebarData({ data, id, position, draggable: true, selectable: false, type: 'step' }));
        dispatch(setSidebarType('addStep'));
    }

    return (
        <>
            {/* <Handle type="source" position={Position.Right} style={{ top: "52%" }} id="conditiona" /> */}
            {/* <Popover open={showMenu} onOpenChange={() => setShowMenu(!showMenu)} content={menuContent} trigger="contextMenu" title="" placement="bottomLeft"> */}
            {
                data?.type === "start" ?
                    <>
                        <div className="start-node cursor-pointer" onClick={handleStartClick}>
                            <PlusOutlined />
                            <span className="veri-label text-[14px] ml-2">{t('Start')}</span>
                        </div>
                        <Handle type="source" position={Position.Right} style={{ top: "24px", right: "0px" }} />
                    </>
                    :
                    <>

                        <div className='node-card'>
                            <Flex className={`node-header ${expanded ? "expanded-node" : ""}`} align='center' justify='space-between'>
                                <Flex justify='space-between' align='center' gap={8}>
                                    <Popover content={colorIconPicker} trigger={"click"} placement="right">
                                        <div className="step-icon-box icon-box cursor-pointer" style={{ backgroundColor: spaceColorBgMapping[data.stepColor] || "#FFF0F6" }}>
                                            {
                                                data.stepIcon ?
                                                    <span style={{ color: data.stepColor, display: "flex" }}>
                                                        <SvgIcons key={"color-" + data.stepColor} type={data.stepIcon} />
                                                    </span>
                                                    :
                                                    <FormOutlined style={{ color: data.stepColor || "#EB2F96" }} />

                                            }

                                        </div>
                                    </Popover>
                                    <Tooltip title={stepName}>
                                        <Input className='p-1 truncate' value={stepName} onChange={(e) => handleNameChange(e.target.value)} />
                                    </Tooltip>
                                </Flex>
                                {
                                    expanded ?
                                        <ChevronDown style={{ color: "gray", width: "24px", height: "24px" }} className='cursor-pointer' onClick={() => setExpanded(false)} />
                                        : <ChevronRight style={{ color: "gray", width: "24px", height: "24px" }} className='cursor-pointer' onClick={() => setExpanded(true)} />
                                }
                            </Flex>
                            {
                                expanded ?
                                <>
                                    <Divider className='margin-0' />
                                    <div className="nodrag nopan" style={{ padding: "8px 12px 0" }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                                        <Select
                                            size="small"
                                            className="nodrag nopan"
                                            style={{ width: "100%" }}
                                            value={editLang}
                                            onChange={setEditLang}
                                            options={FORM_LANGUAGE_OPTIONS}
                                        />
                                    </div>
                                    <Flex className='node-body' >
                                        {
                                            nodeData.formData && nodeData.formData.formId ?
                                                <Flex className='edit-form-button' style={{ backgroundColor: spaceColorBgMapping[data.stepColor] || "#FFF0F6" }} justify='space-between' align='center' onClick={navigateToForm} >
                                                    <div className='node-form-title text-color-primary' >{nodeData.formData.formName}</div>
                                                    <div className='flex gap-2'>
                                                        <EditOutlined style={{ color: data.stepColor || "#EB2F96" }} />
                                                        <DeleteOutlined style={{ color: data.stepColor || "#EB2F96", width:"14px" }} onClick={confirmFormDelete} />
                                                    </div>
                                                </Flex>
                                                :
                                                <Flex className='node-add-button' justify='center' align='center' onClick={navigateToForm}>
                                                    <PlusOutlined style={{ color: "#141BCB" }} />
                                                    <div className="node-form-title" >{t('Add form')}</div>
                                                </Flex>
                                        }
                                    </Flex>
                                </>
                                : null
                            }

                        </div>
                        <Handle type="source" position={Position.Right} style={{ top: "24px" }} />

                    </>
            }
        </>
    )

}

export default InitialNode